import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useHistoricalResultsQuery } from "@/api/vercel";
import { format, subDays } from "date-fns";
import { logger } from "@/utils/logger";
import {
  ChevronDown,
  Copy,
  Download,
  Facebook,
  Frown,
  Instagram,
} from "lucide-react";
import WinningTipModal from "@/components/WinningTipModal";
import BottomNavbar from "@/components/bottom-navbar";
import TrophyIcon from "@/components/icons/trophy-icon";
import XIcon from "@/components/icons/x-icon";
import { Spinner } from "@/components/ui/spinner";
import LexiTipsLogo from "@/assets/lexi-tips-logo.svg";

function SocialPreviews() {
  const [searchParams] = useSearchParams();
  const [timePeriod, setTimePeriod] = useState("today");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [winningTips, setWinningTips] = useState([]);
  const [noDataForCurrentPeriod, setNoDataForCurrentPeriod] = useState(false);
  const [recentDates, setRecentDates] = useState([]);
  const dateFilterContainerRef = useRef(null);
  const noDataFlaggedRef = useRef(false);
  const dataProcessedRef = useRef(false);
  const MAX_RECENT_DATES = 10;
  const [trackData, setTrackData] = useState({});
  const [sortOrder, setSortOrder] = useState("track"); // track, race-asc, race-desc
  const [openModalKey, setOpenModalKey] = useState(null);
  const queryParamsCheckedRef = useRef(false);

  // Helper function to scroll active date button into view
  const scrollActiveDateIntoView = useCallback(() => {
    setTimeout(() => {
      if (dateFilterContainerRef.current) {
        const activeButton = dateFilterContainerRef.current.querySelector(
          ".bg-\\[\\#F30099\\]",
        );
        if (activeButton) {
          activeButton.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      }
    }, 50); // Small timeout to ensure DOM updates
  }, []);

  // Handle time period button click
  const handleTimeChange = useCallback(
    (period, customDate = null) => {
      // Reset the "no data flagged" ref when changing time periods
      noDataFlaggedRef.current = false;
      setTimePeriod(period);

      // If it's a custom date, set the selectedDate
      if (period === "custom" && customDate) {
        setSelectedDate(customDate);
      }

      // Reset the no data flag when changing time periods
      setNoDataForCurrentPeriod(false);

      // Scroll active date into view
      scrollActiveDateIntoView();
    },
    [scrollActiveDateIntoView],
  );

  // Handle date change
  const handleDateChange = (newDate) => {
    // Reset the "no data flagged" ref when changing dates
    noDataFlaggedRef.current = false;
    setSelectedDate(newDate);

    // Update timePeriod based on the new date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // normalize to start of day

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const normalizedNewDate = new Date(newDate);
    normalizedNewDate.setHours(0, 0, 0, 0); // normalize to start of day

    if (normalizedNewDate.getTime() === today.getTime()) {
      setTimePeriod("today");
    } else if (normalizedNewDate.getTime() === yesterday.getTime()) {
      setTimePeriod("yesterday");
    } else {
      // For dates before yesterday, keep as a custom date
      setTimePeriod("custom");
    }

    // Reset the no data flag when changing dates
    setNoDataForCurrentPeriod(false);

    // Scroll active date into view
    scrollActiveDateIntoView();
  };

  // Format date for API query
  const formatDateForQuery = (date) => {
    return format(date, "dd-MM-yyyy");
  };

  // Get query parameters based on current time period
  const getQueryParams = () => {
    const today = new Date();

    if (timePeriod === "today") {
      return {
        date: formatDateForQuery(today),
        fetchAll: false,
      };
    } else if (timePeriod === "yesterday") {
      return {
        date: formatDateForQuery(subDays(today, 1)),
        fetchAll: false,
      };
    } else if (timePeriod === "custom") {
      return {
        date: formatDateForQuery(selectedDate),
        fetchAll: false,
      };
    } else {
      return {
        date: formatDateForQuery(today),
        fetchAll: false,
      };
    }
  };

  // Get historical results data with date filtering
  const {
    data: histResults = { data: {} },
    isError,
    isLoading,
    refetch,
  } = useHistoricalResultsQuery(getQueryParams());
  // Safely get the historical data with a default empty object
  const historicalData = histResults?.data || {};

  // Refetch data when time period changes
  useEffect(() => {
    refetch();
  }, [timePeriod, refetch]);

  // Update selectedDate when timePeriod changes
  useEffect(() => {
    const today = new Date();
    if (timePeriod === "today") {
      setSelectedDate(today);
    } else if (timePeriod === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      setSelectedDate(yesterday);
    }

    // Scroll active date into view whenever timePeriod changes
    scrollActiveDateIntoView();
  }, [timePeriod, scrollActiveDateIntoView]);

  // Process historical data when it loads
  useEffect(() => {
    // State to track current processing cycle
    const currentHistDataId = JSON.stringify({
      dataLength: Object.keys(historicalData || {}).length,
      timePeriod,
      date: selectedDate?.toISOString().split("T")[0],
    });

    // Skip if we've already processed exactly this data (prevents update loop)
    if (dataProcessedRef.current === currentHistDataId) {
      return;
    }

    // Set the processed flag before any state updates to break potential loops
    dataProcessedRef.current = currentHistDataId;

    // Helper to reset the UI state when no data is available
    const resetUIState = () => {
      setWinningTips([]);
      setNoDataForCurrentPeriod(true);
    };

    // If no data is available, handle gracefully
    if (!historicalData || Object.keys(historicalData).length === 0) {
      if (!noDataFlaggedRef.current) {
        noDataFlaggedRef.current = true;
        setNoDataForCurrentPeriod(true);
      }
      resetUIState();
      return;
    }

    try {
      // We have data - reset the no data flag if needed
      if (noDataFlaggedRef.current) {
        noDataFlaggedRef.current = false;
        if (noDataForCurrentPeriod) {
          setNoDataForCurrentPeriod(false);
        }
      }

      // Process data to find winning tips
      const winningTipsArray = [];

      Object.entries(historicalData).forEach(([key, data]) => {
        // Skip entries without prediction or betfair results data
        if (!data.predictionData || !data.betfairResults) {
          return;
        }

        // Check if there's valid firstFourData
        const firstFourData = data.predictionData.firstFourData;
        if (!Array.isArray(firstFourData) || firstFourData.length === 0) {
          return;
        }

        // Get the top tip (first in the array)
        const topTip = firstFourData[0];
        if (!topTip || !topTip.selectionId) {
          return;
        }

        // Check if the top tip won (matches winner selectionId)
        if (data.betfairResults.winner?.selectionId === topTip.selectionId) {
          // Parse the key to get track name and race number
          const parts = key.split("-");
          const raceNumber = parts[parts.length - 4];
          const day = parts[parts.length - 3];
          const month = parts[parts.length - 2];
          const year = parts[parts.length - 1];
          const raceDate = new Date(year, month - 1, day);

          // Track name is all parts before the race number and date
          const trackName = parts.slice(0, parts.length - 4).join(" ");

          // Format horse name and number
          const horseName =
            topTip.formattedName?.split(".").slice(1).join(" ").trim() ||
            "Unknown";
          const horseNumber = topTip.formattedName?.split(".")[0] || "#";

          // Get the second pick if available
          let secondPick = null;
          if (firstFourData.length > 1) {
            const secondTip = firstFourData[1];
            secondPick = {
              horseName:
                secondTip.formattedName?.split(".").slice(1).join(" ").trim() ||
                "Unknown",
              horseNumber: secondTip.formattedName?.split(".")[0] || "#",
              selectionId: secondTip.selectionId,
              odds: secondTip.currentPrice || "N/A",
              silks: secondTip.silks || null,
            };
          }

          // Determine ranking changes - mostly "up" for the winner, "same" for second
          const rankingChanges = {
            [horseNumber]: "up", // Winner typically moved up
          };

          if (secondPick) {
            rankingChanges[secondPick.horseNumber] = "same"; // Second pick typically stayed the same
          }

          // Check if early predictions exist to get actual ranking changes
          const earlyPredictionsKey = `${trackName.replace(/\s+/g, "-")}-${raceNumber}-${day}-${month}-${year}`;
          const earlyPredictions =
            data.earlyPredictionData?.firstFourData || null;

          if (
            earlyPredictions &&
            Array.isArray(earlyPredictions) &&
            earlyPredictions.length > 0
          ) {
            // Here we could compare early vs final predictions to get real ranking changes
            // This would require implementing the getRankingChange logic
          }

          winningTipsArray.push({
            key,
            trackName,
            raceNumber,
            raceDate,
            horseName,
            horseNumber,
            odds: topTip.currentPrice || "N/A",
            silks: topTip.silks || null,
            secondPick,
            rankingChanges,
            tipUpdatedAt: data.predictionData.tipUpdatedAt || "12:00",
            resultedAt: "10:30", // This could be extracted from betfairResults if available
            firstFourData: firstFourData
              .map((tip) => ({
                horseNumber: tip.formattedName?.split(".")[0] || "#",
                horseName:
                  tip.formattedName?.split(".").slice(1).join(" ").trim() ||
                  "Unknown",
                selectionId: tip.selectionId,
                silks: tip.silks || null,
              }))
              .slice(0, 2), // Only get the first two tips
          });
        }
      });

      // Sort winning tips by track name
      winningTipsArray.sort((a, b) => a.trackName.localeCompare(b.trackName));

      // Update state with winning tips
      setWinningTips(winningTipsArray);
      logger.log("winningTipsArray", winningTipsArray);
      // Check query parameters and open modal if matching tip is found
      if (winningTipsArray.length > 0 && !queryParamsCheckedRef.current) {
        queryParamsCheckedRef.current = true;

        // Find matching tip based on track name and race number
        const matchingTip = winningTipsArray.find((tip) => {
          const normalizedTrackName = tip.trackName.toLowerCase().trim();
          const normalizedTrackNameParam = searchParams
            .get("trackname")
            ?.toLowerCase()
            .trim();
          return (
            normalizedTrackName.includes(normalizedTrackNameParam) &&
            tip.raceNumber === searchParams.get("racenumber")
          );
        });

        // If matching tip found, open modal
        if (matchingTip) {
          setTimeout(() => {
            setOpenModalKey(matchingTip.key);
          }, 300); // Small delay to ensure component is fully rendered
        }
      }

      // Handle custom date - add to recent dates if needed
      if (timePeriod === "custom" && winningTipsArray.length > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const normalizedSelectedDate = new Date(selectedDate);
        normalizedSelectedDate.setHours(0, 0, 0, 0);

        if (
          normalizedSelectedDate.getTime() !== today.getTime() &&
          normalizedSelectedDate.getTime() !== yesterday.getTime()
        ) {
          const formattedDate = format(selectedDate, "dd/MM/yyyy");

          // Check if date already exists in recentDates to prevent duplication
          const dateExists = recentDates.some(
            (d) => format(d.date, "dd/MM/yyyy") === formattedDate,
          );

          // Check if the date is among the automatically generated 7-day buttons
          const isInPredefinedDays = [...Array(7)].some((_, i) => {
            const predefinedDate = new Date();
            predefinedDate.setDate(predefinedDate.getDate() - (i + 2)); // i+2 to start 2 days ago (after yesterday)
            predefinedDate.setHours(0, 0, 0, 0);
            return (
              predefinedDate.getTime() === normalizedSelectedDate.getTime()
            );
          });

          // Only add to recentDates if it's not a duplicate and not in the predefined 7-day range
          if (!dateExists && !isInPredefinedDays) {
            const dateObj = { date: selectedDate, formatted: formattedDate };
            // Update recentDates - add the new date and sort properly
            setRecentDates((prevDates) => {
              const newDates = [...prevDates, dateObj];
              return newDates
                .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort in reverse chronological order
                .slice(0, MAX_RECENT_DATES);
            });
          }
        }
      }

      // If we processed data but found no winning tips
      if (winningTipsArray.length === 0) {
        setNoDataForCurrentPeriod(true);
      }
    } catch (error) {
      logger.error("Error processing historical data:", error);
      if (!noDataFlaggedRef.current) {
        noDataFlaggedRef.current = true;
        setNoDataForCurrentPeriod(true);
      }
      resetUIState();
    }
  }, [historicalData, timePeriod, selectedDate, recentDates, searchParams]);

  // Add useEffect to fetch track data from JSON file
  useEffect(() => {
    fetch("/data/horseracing/tracks/track-names.json")
      .then((response) => response.json())
      .then((data) => {
        setTrackData(data);
      })
      .catch((error) => {
        logger.error("Error loading track data:", error);
      });
  }, []);

  // Helper function to get track handle
  const getTrackHandle = (trackName) => {
    if (!trackData || Object.keys(trackData).length === 0) return trackName;

    // Clean up track name to match the format in track-names.json
    const cleanTrackName = trackName.replace(/\s*\([^)]*\)\s*$/, "").trim();

    // Try direct lookup
    if (trackData[cleanTrackName] && trackData[cleanTrackName].handle) {
      return trackData[cleanTrackName].handle;
    }

    // Try to find a partial match
    for (const key of Object.keys(trackData)) {
      if (cleanTrackName.includes(key) || key.includes(cleanTrackName)) {
        if (trackData[key].handle) return trackData[key].handle;
      }
    }

    return trackName; // Return original name as fallback
  };

  // Helper function to get social media handles with fallbacks
  const getSocialHandle = (trackName, platform) => {
    if (!trackData || Object.keys(trackData).length === 0) return "";

    // Clean up track name to match the format in track-names.json
    const cleanTrackName = trackName.replace(/\s*\([^)]*\)\s*$/, "").trim();

    // Try direct lookup
    if (trackData[cleanTrackName]) {
      // Get the appropriate handle based on platform
      if (platform === "inst" && trackData[cleanTrackName].inst) {
        return trackData[cleanTrackName].inst;
      } else if (platform === "x" && trackData[cleanTrackName].x) {
        return trackData[cleanTrackName].x;
      } else if (
        platform === "facebook" &&
        trackData[cleanTrackName].facebook
      ) {
        return trackData[cleanTrackName].facebook;
      }

      // First fallback: use hashtag if available
      if (trackData[cleanTrackName].hashtag) {
        return trackData[cleanTrackName].hashtag;
      }

      // Second fallback: use City
      if (trackData[cleanTrackName].City) {
        return trackData[cleanTrackName].City;
      }
    }

    // Try to find a partial match
    for (const key of Object.keys(trackData)) {
      if (cleanTrackName.includes(key) || key.includes(cleanTrackName)) {
        const track = trackData[key];
        if (platform === "inst" && track.inst) {
          return track.inst;
        } else if (platform === "x" && track.x) {
          return track.x;
        } else if (platform === "facebook" && track.facebook) {
          return track.facebook;
        } else if (track.hashtag) {
          return track.hashtag;
        } else if (track.City) {
          return track.City;
        }
      }
    }

    return trackName; // Return original name as final fallback
  };

  // Handle sort change
  const handleSortChange = (order) => {
    setSortOrder(order);
  };

  // Apply sorting whenever sort order or winning tips change
  useEffect(() => {
    if (!winningTips.length) return;

    const sortedTips = [...winningTips];
    if (sortOrder === "track") {
      sortedTips.sort((a, b) => a.trackName.localeCompare(b.trackName));
    } else if (sortOrder === "race-asc") {
      sortedTips.sort((a, b) => a.raceNumber.localeCompare(b.raceNumber));
    } else if (sortOrder === "race-desc") {
      sortedTips.sort((a, b) => b.raceNumber.localeCompare(a.raceNumber));
    }

    setWinningTips(sortedTips);
  }, [sortOrder]);

  // Check query parameters and open modal if matching tip is found
  useEffect(() => {
    // Get query parameters if present
    const trackNameParam = searchParams.get("trackname");
    const raceNumberParam = searchParams.get("racenumber");

    if (
      trackNameParam &&
      raceNumberParam &&
      winningTips.length > 0 &&
      !queryParamsCheckedRef.current
    ) {
      queryParamsCheckedRef.current = true;

      // Find matching tip based on track name and race number
      const matchingTip = winningTips.find((tip) => {
        const normalizedTrackName = tip.trackName.toLowerCase().trim();
        const normalizedTrackNameParam = trackNameParam.toLowerCase().trim();
        return (
          normalizedTrackName.includes(normalizedTrackNameParam) &&
          tip.raceNumber === raceNumberParam
        );
      });

      // If matching tip found, open modal
      if (matchingTip) {
        setTimeout(() => {
          setOpenModalKey(matchingTip.key);
        }, 300); // Small delay to ensure component is fully rendered
      }
    }
  }, [winningTips, searchParams]);

  const handleCopyToClipboard = (tip, platform = "x") => {
    let handle = "";

    // Get the appropriate handle based on platform
    if (platform === "inst") {
      handle = getSocialHandle(tip.trackName, "inst");
      handle = handle
        ? `@${handle}`
        : `#${getSocialHandle(tip.trackName, "x") || tip.trackName.replace(/\s+/g, "")}`;
    } else if (platform === "x") {
      handle = getSocialHandle(tip.trackName, "x");
      handle = handle ? `@${handle}` : `#${tip.trackName.replace(/\s+/g, "")}`;
    } else if (platform === "facebook") {
      handle = getSocialHandle(tip.trackName, "facebook");
      handle = handle ? `@${handle}` : `#${tip.trackName.replace(/\s+/g, "")}`;
    }

    // Format the social text message with the appropriate handle
    const text = `Another Winner tipped by Lexi. #${tip.horseNumber} ${tip.horseName} wins Race ${tip.raceNumber} at ${handle}

Get your tips here https://lexi.tips and get in on the action.`;

    // Copy to clipboard
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert(
          `Copied to clipboard for ${platform === "inst" ? "Instagram" : platform === "x" ? "X" : "Facebook"}!`,
        );
      })
      .catch((err) => {
        logger.error("Failed to copy text: ", err);
      });
  };

  // Function to handle download from the card (without modal being open)
  const handleCardDownload = (tipKey, event) => {
    event.stopPropagation();
    event.preventDefault();

    // Open the modal first
    setOpenModalKey(tipKey);

    // Wait a bit for the modal to render and trigger download programmatically
    setTimeout(() => {
      const downloadBtn = document.getElementById("modal-download-btn");
      if (downloadBtn) downloadBtn.click();
    }, 100);
  };

  // Render error state
  if (isError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-red-500">Error loading winning tips</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Social Previews | Lexi Tips</title>
        <meta name="description" content="Internal tool for social media content generation" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen h-screen bg-gray-50 pb-16">
        <div className="max-w-md mx-auto h-full flex flex-col">
        {/* Date Carousel */}
        <div
          ref={dateFilterContainerRef}
          className="date-filter-container px-4 pt-4 pb-2 bg-white overflow-x-scroll overflow-y-hidden"
        >
          <div className="flex flex-row-reverse space-x-2 space-x-reverse min-w-max">
            <button
              type="button"
              className={`px-4 py-2 rounded-full text-sm font-medium ${timePeriod === "today"
                  ? "bg-[#F30099] text-white"
                  : "bg-gray-200 text-gray-700"
                }`}
              onClick={() => handleTimeChange("today")}
              data-gtm-element-id="winning-tips_date-filter_button-today"
            >
              TODAY
            </button>

            <button
              type="button"
              className={`px-4 py-2 rounded-full text-sm font-medium ${timePeriod === "yesterday"
                  ? "bg-[#F30099] text-white"
                  : "bg-gray-200 text-gray-700"
                }`}
              onClick={() => handleTimeChange("yesterday")}
              data-gtm-element-id="winning-tips_date-filter_button-yesterday"
            >
              YESTERDAY
            </button>

            {/* Previous 7 days buttons */}
            {[...Array(7)].map((_, i) => {
              const date = new Date();
              date.setDate(date.getDate() - (i + 2)); // i+2 to start 2 days ago (after yesterday)
              const dateStr = format(date, "dd/MM/yyyy");
              const displayStr = format(date, "dd MMM");

              return (
                <button
                  key={dateStr}
                  type="button"
                  className={`px-4 py-2 rounded-full text-sm font-medium ${timePeriod === "custom" &&
                      format(selectedDate, "dd/MM/yyyy") === dateStr
                      ? "bg-[#F30099] text-white"
                      : "bg-gray-200 text-gray-700"
                    }`}
                  onClick={() => handleTimeChange("custom", date)}
                  data-gtm-element-id={`winning-tips_date-filter_button-${format(date, "dd-MM")}`}
                >
                  {displayStr}
                </button>
              );
            })}

            {/* Recent dates, sorted chronologically (newest to oldest) */}
            {recentDates
              .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort in reverse chronological order
              .map((item) => (
                <button
                  key={item.formatted}
                  type="button"
                  className={`px-4 py-2 rounded-full text-sm font-medium ${timePeriod === "custom" &&
                      format(selectedDate, "dd/MM/yyyy") === item.formatted
                      ? "bg-[#F30099] text-white"
                      : "bg-gray-200 text-gray-700"
                    }`}
                  onClick={() =>
                    handleTimeChange("custom", new Date(item.date))
                  }
                  data-gtm-element-id={`winning-tips_date-filter_button-${format(new Date(item.date), "dd-MM")}`}
                >
                  {format(new Date(item.date), "dd MMM")}
                </button>
              ))}
          </div>
        </div>

        {/* Page Header with Sort */}
        <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">
              Winning Tips{" "}
              <span className="bg-pink-100 rounded-full px-2 py-1 font-medium text-brand-pink">
                {winningTips.length}
              </span>
            </h1>
            <p className="text-sm text-gray-600">
              Races where our top pick won
            </p>
          </div>

          <div className="relative">
            <button
              className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium flex items-center gap-1 bg-white hover:bg-gray-50"
              onClick={() =>
                document
                  .getElementById("sort-dropdown")
                  .classList.toggle("hidden")
              }
              data-gtm-element-id="winning-tips_sort_button"
            >
              Sort <ChevronDown className="h-3.5 w-3.5" />
            </button>

            <div
              id="sort-dropdown"
              className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 hidden"
            >
              <button
                className={`block px-4 py-2 text-sm w-full text-left ${sortOrder === "track" ? "bg-gray-100" : ""}`}
                onClick={() => {
                  handleSortChange("track");
                  document
                    .getElementById("sort-dropdown")
                    .classList.add("hidden");
                }}
                data-gtm-element-id="winning-tips_sort_option-track"
              >
                By Track
              </button>
              <button
                className={`block px-4 py-2 text-sm w-full text-left ${sortOrder === "race-asc" ? "bg-gray-100" : ""}`}
                onClick={() => {
                  handleSortChange("race-asc");
                  document
                    .getElementById("sort-dropdown")
                    .classList.add("hidden");
                }}
                data-gtm-element-id="winning-tips_sort_option-race-asc"
              >
                By Race (Ascending)
              </button>
              <button
                className={`block px-4 py-2 text-sm w-full text-left ${sortOrder === "race-desc" ? "bg-gray-100" : ""}`}
                onClick={() => {
                  handleSortChange("race-desc");
                  document
                    .getElementById("sort-dropdown")
                    .classList.add("hidden");
                }}
                data-gtm-element-id="winning-tips_sort_option-race-desc"
              >
                By Race (Descending)
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex h-full flex-1 items-center justify-center">
            <Spinner size="large" className="text-[#F30099]" />
          </div>
        ) : (
          <div className="winning-tips-content flex-1 overflow-y-auto p-4 space-y-4">
            {noDataForCurrentPeriod || winningTips.length === 0 ? (
              <div className="no-results-container h-full flex flex-col items-center justify-center py-10">
                <Frown className="w-12 h-12 text-gray-400 mb-2" />
                <p className="text-md font-medium text-gray-700">
                  No winning tips found
                </p>
                <p className="text-sm text-gray-500 text-center px-4">
                  {noDataForCurrentPeriod
                    ? `No data for ${format(selectedDate, "dd MMM yyyy")}`
                    : "No races where our top pick won."}
                </p>

                <div className="mt-6 flex flex-col items-center">
                  <p className="text-sm mb-2">Try a different date:</p>
                  <div className="flex space-x-3">
                    {timePeriod !== "yesterday" && (
                      <button
                        className="px-6 py-2 bg-[#F30099] text-white rounded-full text-sm font-medium"
                        onClick={() => handleTimeChange("yesterday")}
                        data-gtm-element-id="winning-tips_empty-state_button-yesterday"
                      >
                        YESTERDAY
                      </button>
                    )}
                    {timePeriod !== "today" && (
                      <button
                        className="px-6 py-2 bg-[#F30099] text-white rounded-full text-sm font-medium"
                        onClick={() => handleTimeChange("today")}
                        data-gtm-element-id="winning-tips_empty-state_button-today"
                      >
                        TODAY
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {winningTips.map((tip) => (
                  <div
                    key={tip.key}
                    className="bg-white rounded-lg shadow overflow-hidden"
                    data-gtm-element-id={`winning-tip-${tip.trackName.replace(/\s+/g, "-").toLowerCase()}-${tip.raceNumber}`}
                  >
                    <div className="flex">
                      {/* Image area on the left - now a preview that opens modal */}
                      <div
                        className="w-48 h-48 m-2 bg-[#FCEDF7] flex flex-col items-center justify-center shrink-0 cursor-pointer hover:bg-pink-100 transition-all rounded-md overflow-hidden relative"
                        onClick={() => setOpenModalKey(tip.key)}
                        data-gtm-element-id={`winning-tip-image-${tip.trackName.replace(/\s+/g, "-").toLowerCase()}-${tip.raceNumber}`}
                      >
                        {/* Mini preview of social image */}
                        <div className="absolute inset-0 w-full h-full  flex flex-col items-center justify-center p-1 text-center">
                          <span className="text-brand-pink text-[8px] font-bold leading-tight">
                            ANOTHER WINNER!
                          </span>
                          <span className="text-black text-[10px] font-bold mt-0.5 leading-tight">
                            #{tip.horseNumber}{" "}
                            {tip.horseName.length > 25
                              ? tip.horseName.substring(0, 25) + "..."
                              : tip.horseName}
                          </span>
                          <span className="text-black text-[7px] mt-0.5 leading-tight">
                            {tip.trackName} R{tip.raceNumber}
                          </span>
                          <div className="absolute bottom-1 right-1 text-[7px] font-bold">
                            <span className="text-brand-pink">Lexi</span>
                            <span className="text-black">.tips</span>
                          </div>
                        </div>
                      </div>

                      {/* Track and horse info on the right */}
                      <div className="p-3 flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-base">
                              {tip.trackName} R{tip.raceNumber}
                            </h3>
                            <div className="flex items-center mt-1">
                              <span className="bg-pink-100 rounded-full px-2 py-0.5 text-sm font-medium text-brand-pink">
                                #{tip.horseNumber}
                              </span>
                              &nbsp;
                              <span className="font-medium truncate w-[70%]">
                                {tip.horseName}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Social text area */}
                    <div className="px-4 py-2 border-t border-gray-200 bg-gray-50/50">
                      <p className="text-xs text-gray-500">
                        Another Winner tipped by Lexi. #{tip.horseNumber}{" "}
                        {tip.horseName} wins Race {tip.raceNumber} at @
                        {getTrackHandle(tip.trackName)}
                        <br />
                        <br />
                        Get your tips here https://lexi.tips and get in on the action.
                      </p>
                    </div>

                    {/* Download and copy buttons */}
                    <div className="flex border-t border-gray-200">
                      <button
                        className="flex-1 py-2 flex items-center justify-center gap-2 text-sm text-gray-600 hover:bg-gray-50"
                        onClick={(e) => handleCopyToClipboard(tip, "inst")}
                        data-gtm-element-id={`winning-tip-copy-${tip.trackName.replace(/\s+/g, "-").toLowerCase()}-${tip.raceNumber}-inst`}
                      >
                        <Instagram className="h-4 w-4" />
                        Copy
                      </button>
                      <div className="w-px bg-gray-200"></div>
                      <button
                        className="flex-1 py-2 flex items-center justify-center gap-2 text-sm text-gray-600 hover:bg-gray-50"
                        onClick={(e) => handleCopyToClipboard(tip, "x")}
                        data-gtm-element-id={`winning-tip-copy-${tip.trackName.replace(/\s+/g, "-").toLowerCase()}-${tip.raceNumber}-x`}
                      >
                        <XIcon className="h-4 w-4" />
                        Copy
                      </button>
                      <div className="w-px bg-gray-200"></div>
                      <button
                        className="flex-1 py-2 flex items-center justify-center gap-2 text-sm text-gray-600 hover:bg-gray-50"
                        onClick={(e) => handleCopyToClipboard(tip, "facebook")}
                        data-gtm-element-id={`winning-tip-copy-${tip.trackName.replace(/\s+/g, "-").toLowerCase()}-${tip.raceNumber}-facebook`}
                      >
                        <Facebook className="h-4 w-4" />
                        Copy
                      </button>
                      <div className="w-px bg-gray-200"></div>
                      <button
                        className="flex-1 py-2 flex items-center justify-center gap-2 text-sm text-gray-600 hover:bg-gray-50"
                        onClick={(e) => handleCardDownload(tip.key, e)}
                        data-gtm-element-id={`winning-tip-download-${tip.trackName.replace(/\s+/g, "-").toLowerCase()}-${tip.raceNumber}`}
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <BottomNavbar />

      {/* Using the WinningTipModal component */}
      {openModalKey && (
        <WinningTipModal
          tip={winningTips.find((t) => t.key === openModalKey)}
          isOpen={!!openModalKey}
          onClose={() => setOpenModalKey(null)}
          historicalData={historicalData}
          selectedDate={formatDateForQuery(selectedDate)}
        />
      )}
      </div>
    </>
  );
}

export default SocialPreviews;
